import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  MenuItem,
  Pagination,
  Fade,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Button,
} from '@mui/material';
import {
  Search,
  MedicalInformation,
  Person,
  LocalHospital,
  AttachMoney,
  Event,
  FilterList,
  Description,
  Add as AddIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import consultationService from '../services/consultationService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PrescriptionModal from './PrescriptionModal';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 10;
const TYPE_OPTIONS = [
  { value: 'all', label: 'Tous les types' },
  { value: 'clinique', label: 'Clinique' },
  { value: 'clinique_labo', label: 'Clinique & Laboratoire' },
];

const formatDate = (dateString) => {
  return format(new Date(dateString), "d MMMM yyyy 'à' HH:mm", { locale: fr });
};

const ConsultationCard = ({ consultation, onPrescribe }) => (
  <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'primary.main', '&:hover': { boxShadow: 6 } }}>
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocalHospital sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div">
              {consultation.label}
              <Chip 
                icon={<MedicalInformation />}
                label={consultation.type === 'clinique' ? 'Clinique' : 'Clinique & Labo'} 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }}
              />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
            <Description sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {consultation.description || 'Aucune description'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Person sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="body2">
              <strong>Patient:</strong> {consultation.reception.assure.nom} {consultation.reception.assure.prenom}
              <Chip 
                label={`Matricule: ${consultation.reception.assure.matricule}`} 
                size="small" 
                sx={{ ml: 1, backgroundColor: 'primary.light', color: 'white' }}
              />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AttachMoney sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Prix:</strong> {consultation.prix.toLocaleString()} FCFA
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Event sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {formatDate(consultation.createdAt)}
            </Typography>
          </Box>
        </Grid>
        
        {consultation.MedecinClinique && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Person sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="body2">
                <strong>Médecin:</strong> Dr. {consultation.MedecinClinique.nom} {consultation.MedecinClinique.prenom}
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => onPrescribe(consultation)}
            >
              Nouvelle Prescription
            </Button>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

const ConsultationsHistory = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);

  const fetchConsultations = async (pageNumber, search = '', type = 'all') => {
    setLoading(true);
    try {
      const filters = {
        page: pageNumber,
        limit: ITEMS_PER_PAGE,
        search: search?.trim(),
        ...(type !== 'all' && { type })
      };

      const response = await consultationService.getAllConsultations(pageNumber, ITEMS_PER_PAGE, filters);
      if (response?.data) {
        setConsultations(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
        setError(null);
      }
    } catch (err) {
      setError("Erreur lors du chargement des consultations");
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useMemo(
    () => debounce(fetchConsultations, 300),
    []
  );

  useEffect(() => {
    debouncedFetch(page, searchTerm, typeFilter);
    return () => debouncedFetch.cancel();
  }, [page, searchTerm, typeFilter]);

  const handlePrescribe = (consultation) => {
    setSelectedConsultation(consultation);
    setPrescriptionModalOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MedicalInformation sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Historique des Consultations
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher par nom, matricule..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Type de consultation</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Type de consultation"
              startAdornment={
                <InputAdornment position="start">
                  <FilterList color="primary" />
                </InputAdornment>
              }
            >
              {TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : consultations.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          Aucune consultation trouvée
        </Alert>
      ) : (
        <>
          <Fade in={true}>
            <Box>
              {consultations.map((consultation) => (
                <ConsultationCard 
                  key={consultation.id} 
                  consultation={consultation}
                  onPrescribe={handlePrescribe}
                />
              ))}
            </Box>
          </Fade>

          {totalPages > 1 && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      <PrescriptionModal
        open={prescriptionModalOpen}
        onClose={() => setPrescriptionModalOpen(false)}
        consultation={selectedConsultation}
        onSuccess={() => {
          setPrescriptionModalOpen(false);
          setSelectedConsultation(null);
          toast.success('Prescription créée avec succès');
        }}
      />
    </Box>
  );
};

export default ConsultationsHistory;
